import type {CachesIndexPayload} from '@github-ui/dotcom-schema/types/actions/v1/caches_index'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {SoftNavOnlyLink, useLocation, useNavigate} from '@github-ui/react-router'
import {SimpleListView} from '@github-ui/simple-list-view'
import {SimpleListHeader} from '@github-ui/simple-list-view/SimpleListHeader'
import {useAnalytics} from '@github-ui/use-analytics'
import {Link, Pagination} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useCallback} from 'react'
import {CachesListItem} from './CachesListItem'
import {CachesListHeader} from './CachesListHeader'
import {CachesFilter} from './CachesFilter'
import styles from './CachesList.module.css'

export function CachesList({payload, onDeleted}: {payload: CachesIndexPayload; onDeleted?: () => Promise<void>}) {
  const location = useLocation()
  const navigate = useNavigate()
  const query = new URLSearchParams(location.search).get('query') ?? payload.query
  const updateQuery = useCallback(
    (value: string) => {
      const params = new URLSearchParams(location.search)
      const nextQuery = value.trim()
      if (nextQuery) params.set('query', nextQuery)
      else params.delete('query')
      params.delete('page')
      const search = params.toString()
      navigate(`${payload.cachesPath || location.pathname}${search ? `?${search}` : ''}`, {preventAutofocus: true})
    },
    [location.pathname, location.search, navigate, payload.cachesPath],
  )
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const track = useCallback(
    (action: string) =>
      sendAnalyticsEvent('analytics.click', undefined, {
        category: 'ACTIONS_WORKFLOWS',
        action: `caches.${action}`,
        label: 'ref_loc:actions_caches',
      }),
    [sendAnalyticsEvent],
  )
  const pageHref = (page: number) => {
    const params = new URLSearchParams(location.search)
    if (page === 1) params.delete('page')
    else params.set('page', String(page))
    const search = params.toString()
    return `${payload.cachesPath || location.pathname}${search ? `?${search}` : ''}`
  }
  const filtered = payload.query.trim().length > 0

  return (
    <>
      <FilteredListPageLayout.FilterBar aria-label="Filter caches">
        <CachesFilter branchesPath={payload.branchesPath} query={query} onQueryChange={updateQuery} track={track} />
      </FilteredListPageLayout.FilterBar>
      <SimpleListView>
        <SimpleListHeader.Title headingLevel="h2" className="sr-only">
          Caches
        </SimpleListHeader.Title>
        <CachesListHeader payload={payload} query={query} onQueryChange={updateQuery} track={track} />
        {payload.cacheItems.length > 0 ? (
          <SimpleListView.Items>
            {payload.cacheItems.map(cache => (
              <CachesListItem
                key={cache.id.toString()}
                cache={cache}
                canDelete={payload.canDelete}
                track={track}
                onDeleted={onDeleted}
              />
            ))}
          </SimpleListView.Items>
        ) : (
          <Blankslate>
            <Blankslate.Heading as="h2">{filtered ? 'No caches matched your search' : 'No caches'}</Blankslate.Heading>
            <Blankslate.Description>
              {filtered ? (
                <span className={styles.Text}>No caches matched your search {payload.query}</span>
              ) : (
                <span>Nothing has been cached by workflows running in this repository.</span>
              )}
            </Blankslate.Description>
            <Blankslate.Description>
              {filtered ? (
                <>
                  <Link as={SoftNavOnlyLink} to={payload.cachesPath} inline onClick={() => track('remove_filters')}>
                    Remove the filters
                  </Link>{' '}
                  <span>or try a different search query.</span>
                </>
              ) : (
                <>
                  <Link href={payload.cachingHelpUrl} inline onClick={() => track('learn_more')}>
                    Learn more about caching
                  </Link>{' '}
                  <span>dependencies and build outputs to improve workflow execution time.</span>
                </>
              )}
            </Blankslate.Description>
          </Blankslate>
        )}
      </SimpleListView>
      {payload.totalPages > 1 && (
        <div className="d-flex flex-justify-center mt-4">
          <Pagination
            currentPage={payload.page}
            pageCount={payload.totalPages}
            hrefBuilder={pageHref}
            renderPage={({key, number, href, onClick: _onClick, ...props}) =>
              href ? (
                <SoftNavOnlyLink key={key} to={pageHref(number)} {...props} onClick={() => track('paginate')} />
              ) : (
                <span key={key} {...props} />
              )
            }
          />
        </div>
      )}
    </>
  )
}
